const EARTH_METERS_PER_DEG_LAT = 110540;
const GOOGLE_SOLAR_DSM_NO_DATA_SENTINEL = -9999;
const GOOGLE_SOLAR_DSM_NO_DATA_EPS = 1e-3;
const GOOGLE_SOLAR_MIN_PIXEL_SIZE_METERS = 0.1;
const EARTH_RADIUS_METERS = 6378137;
const WEB_MERCATOR_MAX_EXTENT_METERS = EARTH_RADIUS_METERS * Math.PI;
const MAX_MERCATOR_LAT = 85.05112878;
const WEB_MERCATOR_EPSG_CODES = new Set([3857, 900913, 102100, 102113, 3785]);
const GEO_TIFF_AXIS_EPS = 1e-12;
const GEO_TIFF_BOUNDS_MISMATCH_RATIO = 0.05;
// const SOLAR_UTILS_LOG_PREFIX = "[LIDAR][GoogleSolarUtils]";

const DEFAULT_MONTHLY_WEIGHTS = [
  0.58, 0.66, 0.82, 0.96, 1.08, 1.14,
  1.18, 1.08, 0.94, 0.82, 0.66, 0.56,
];

const toFiniteNumber = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeLongitude = (longitude) => {
  let normalized = toFiniteNumber(longitude, 0);
  while (normalized > 180) normalized -= 360;
  while (normalized < -180) normalized += 360;
  return normalized;
};

const circularDiffDegrees = (a, b) => {
  const lhs = normalizeLongitude(a);
  const rhs = normalizeLongitude(b);
  let diff = Math.abs(lhs - rhs);
  if (diff > 180) diff = 360 - diff;
  return diff;
};

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const metersPerDegreeLngAtLat = (latitude) => {
  const latRad = (toFiniteNumber(latitude, 0) * Math.PI) / 180;
  return Math.max(1e-6, 111320 * Math.cos(latRad));
};

const latLngToLocalMeters = (latitude, longitude, anchorLat, anchorLng) => {
  const lat = toFiniteNumber(latitude, anchorLat);
  const lng = toFiniteNumber(longitude, anchorLng);
  const east = (lng - anchorLng) * metersPerDegreeLngAtLat(anchorLat);
  const north = (lat - anchorLat) * EARTH_METERS_PER_DEG_LAT;
  return { east, north };
};

const geoReferencePixelToGeo = (geoReference, col, row) => {
  if (!geoReference) return null;
  const originX = toFiniteNumber(geoReference.originX, null);
  const originY = toFiniteNumber(geoReference.originY, null);
  const pixelSizeX = toFiniteNumber(geoReference.pixelSizeX, null);
  const pixelSizeY = toFiniteNumber(geoReference.pixelSizeY, null);
  if (
    originX == null ||
    originY == null ||
    pixelSizeX == null ||
    pixelSizeY == null
  ) {
    return null;
  }

  return {
    x: originX + col * pixelSizeX,
    y: originY + row * pixelSizeY,
  };
};

const geoReferencePixelToLatLng = (geoReference, col, row) => {
  const point = geoReferencePixelToGeo(geoReference, col, row);
  if (!point) return null;

  if (geoReference.coordinateSpace === "web-mercator-meters") {
    return {
      latitude: mercatorMetersToLatitude(point.y),
      longitude: mercatorMetersToLongitude(point.x),
    };
  }

  if (geoReference.coordinateSpace === "geographic") {
    return {
      latitude: point.y,
      longitude: point.x,
    };
  }

  return null;
};

const getGeoReferenceLocalMetersProjector = (geoReference, anchorLat, anchorLng) => {
  if (!geoReference) return null;

  const anchorLatLng = geoReferencePixelToLatLng(
    geoReference,
    (Math.max(2, toFiniteNumber(geoReference.widthPx, 2)) - 1) * 0.5,
    (Math.max(2, toFiniteNumber(geoReference.heightPx, 2)) - 1) * 0.5
  );

  if (anchorLatLng) {
    return (col, row) => {
      const latLng = geoReferencePixelToLatLng(geoReference, col, row);
      if (!latLng) return null;
      return latLngToLocalMeters(
        latLng.latitude,
        latLng.longitude,
        anchorLat,
        anchorLng
      );
    };
  }

  const centerCol = (Math.max(2, toFiniteNumber(geoReference.widthPx, 2)) - 1) * 0.5;
  const centerRow = (Math.max(2, toFiniteNumber(geoReference.heightPx, 2)) - 1) * 0.5;
  const centerPoint = geoReferencePixelToGeo(geoReference, centerCol, centerRow);
  if (!centerPoint) return null;

  return (col, row) => {
    const point = geoReferencePixelToGeo(geoReference, col, row);
    if (!point) return null;
    return {
      east: point.x - centerPoint.x,
      north: point.y - centerPoint.y,
    };
  };
};

const logSolarUtilsInfo = (...parts) => {
  // console.info(SOLAR_UTILS_LOG_PREFIX, ...parts);
};

const logSolarUtilsWarn = (...parts) => {
  // console.warn(SOLAR_UTILS_LOG_PREFIX, ...parts);
};

let geoTiffModulePromise = null;

const getGeoTiffModule = async () => {
  if (!geoTiffModulePromise) {
    geoTiffModulePromise = new Promise((resolve, reject) => {
      const existingModule = window?.GeoTIFF;
      if (existingModule?.fromArrayBuffer) {
        resolve(existingModule);
        return;
      }

      const existingScript = document.querySelector('script[data-geotiff-browser="true"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window?.GeoTIFF));
        existingScript.addEventListener("error", () => reject(new Error("Failed to load GeoTIFF browser bundle.")));
        return;
      }

      const candidateSources = [
        "https://cdn.jsdelivr.net/npm/geotiff@2.1.3/dist-browser/geotiff.min.js",
        "https://unpkg.com/geotiff@2.1.3/dist-browser/geotiff.min.js",
      ];

      const tryLoadScript = (index = 0) => {
        if (index >= candidateSources.length) {
          reject(new Error("Failed to load GeoTIFF browser bundle."));
          return;
        }

        const existing = document.querySelector(
          `script[data-geotiff-browser-source="${candidateSources[index]}"]`
        );
        if (existing) {
          existing.addEventListener("load", () => resolve(window?.GeoTIFF));
          existing.addEventListener("error", () => tryLoadScript(index + 1));
          return;
        }

        const script = document.createElement("script");
        script.src = candidateSources[index];
        script.async = true;
        script.dataset.geotiffBrowser = "true";
        script.dataset.geotiffBrowserSource = candidateSources[index];
        script.onload = () => {
          if (window?.GeoTIFF?.fromArrayBuffer) {
            resolve(window.GeoTIFF);
            return;
          }
          tryLoadScript(index + 1);
        };
        script.onerror = () => tryLoadScript(index + 1);
        document.head.appendChild(script);
      };

      tryLoadScript(0);
    });
  }
  return geoTiffModulePromise;
};

const parseGeoTiffNoData = (rawValue) => {
  if (rawValue == null) return null;
  if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();
    if (!trimmed) return null;
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const isNoDataValue = (value, noDataValue) => {
  if (!Number.isFinite(value)) return true;
  if (noDataValue != null && Math.abs(value - noDataValue) <= GOOGLE_SOLAR_DSM_NO_DATA_EPS) {
    return true;
  }
  return Math.abs(value - GOOGLE_SOLAR_DSM_NO_DATA_SENTINEL) <= GOOGLE_SOLAR_DSM_NO_DATA_EPS;
};

const toArray = (value) => (Array.isArray(value) ? value : value ? Array.from(value) : []);

const clampLatitude = (latitude) =>
  Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, latitude));

const mercatorMetersToLongitude = (xMeters) =>
  (toFiniteNumber(xMeters, 0) / EARTH_RADIUS_METERS) * (180 / Math.PI);

const mercatorMetersToLatitude = (yMeters) => {
  const latRad = 2 * Math.atan(Math.exp(toFiniteNumber(yMeters, 0) / EARTH_RADIUS_METERS)) - Math.PI / 2;
  return clampLatitude((latRad * 180) / Math.PI);
};

const classifyGeoTiffCoordinateSpace = (geoKeys, originX, originY, pixelSizeX, pixelSizeY) => {
  const projectedCode = Number(
    geoKeys?.ProjectedCSTypeGeoKey ??
      geoKeys?.ProjectedCRSGeoKey ??
      NaN
  );
  const geographicCode = Number(
    geoKeys?.GeographicTypeGeoKey ?? geoKeys?.GeodeticCRSGeoKey ?? NaN
  );

  if (Number.isFinite(projectedCode) && WEB_MERCATOR_EPSG_CODES.has(projectedCode)) {
    return "web-mercator-meters";
  }

  const looksLikeInferredWebMercator =
    !Number.isFinite(projectedCode) &&
    Math.abs(originX) >= 1_000_000 &&
    Math.abs(originY) >= 1_000_000 &&
    Math.abs(originX) <= WEB_MERCATOR_MAX_EXTENT_METERS * 1.05 &&
    Math.abs(originY) <= WEB_MERCATOR_MAX_EXTENT_METERS * 1.05 &&
    Math.abs(pixelSizeX) > GEO_TIFF_AXIS_EPS &&
    Math.abs(pixelSizeY) > GEO_TIFF_AXIS_EPS &&
    Math.abs(pixelSizeX) <= 50 &&
    Math.abs(pixelSizeY) <= 50;
  if (looksLikeInferredWebMercator) {
    return "web-mercator-meters";
  }

  if (Number.isFinite(projectedCode)) {
    return "projected-meters-unknown";
  }

  const looksLikeGeographic =
    Math.abs(originX) <= 180.5 &&
    Math.abs(originY) <= 90.5 &&
    Math.abs(pixelSizeX) <= 1 &&
    Math.abs(pixelSizeY) <= 1;

  if (looksLikeGeographic) return "geographic";
  if (Number.isFinite(geographicCode) && geographicCode === 4326) return "geographic";

  const looksLikeProjectedMeters =
    Math.abs(originX) > 1000 ||
    Math.abs(originY) > 1000 ||
    Math.abs(pixelSizeX) > 1 ||
    Math.abs(pixelSizeY) > 1;
  if (looksLikeProjectedMeters) return "projected-meters-unknown";

  return "geographic";
};

const getGeoTiffBoundsFromImage = (image) => {
  const rawBounds =
    typeof image?.getBoundingBox === "function" ? image.getBoundingBox() : null;
  const minX = toFiniteNumber(rawBounds?.[0], null);
  const minY = toFiniteNumber(rawBounds?.[1], null);
  const maxX = toFiniteNumber(rawBounds?.[2], null);
  const maxY = toFiniteNumber(rawBounds?.[3], null);
  if (minX == null || minY == null || maxX == null || maxY == null) return null;
  if (Math.abs(maxX - minX) <= GEO_TIFF_AXIS_EPS || Math.abs(maxY - minY) <= GEO_TIFF_AXIS_EPS) {
    return null;
  }
  return { minX, minY, maxX, maxY };
};

const deriveAffineFromGeoBounds = (
  bounds,
  width,
  height,
  signPrefs = { x: 1, y: -1 }
) => {
  if (!bounds || !Number.isFinite(width) || !Number.isFinite(height)) return null;
  const spanX = bounds.maxX - bounds.minX;
  const spanY = bounds.maxY - bounds.minY;
  if (Math.abs(spanX) <= GEO_TIFF_AXIS_EPS || Math.abs(spanY) <= GEO_TIFF_AXIS_EPS) return null;

  const xSign = signPrefs?.x < 0 ? -1 : 1;
  const ySign = signPrefs?.y > 0 ? 1 : -1;
  const pxX = xSign * Math.abs(spanX / Math.max(width - 1, 1));
  const pxY = ySign * Math.abs(spanY / Math.max(height - 1, 1));

  return {
    originX: xSign > 0 ? bounds.minX : bounds.maxX,
    originY: ySign > 0 ? bounds.minY : bounds.maxY,
    pixelSizeX: pxX,
    pixelSizeY: pxY,
  };
};

const deriveGeoReferenceFromGeoTiffImage = (image, geoKeys = null) => {
  if (!image) return null;

  const width = toFiniteNumber(image.getWidth?.(), null);
  const height = toFiniteNumber(image.getHeight?.(), null);
  if (width == null || height == null || width < 2 || height < 2) return null;

  let originX = null;
  let originY = null;
  let pixelSizeX = null;
  let pixelSizeY = null;
  let derivedFrom = null;

  const fileDirectory = image.fileDirectory || {};
  const modelTransformation = toArray(fileDirectory.ModelTransformation);
  const modelTiepoint = toArray(fileDirectory.ModelTiepoint);
  const modelPixelScale = toArray(fileDirectory.ModelPixelScale);
  const geoBounds = getGeoTiffBoundsFromImage(image);

  if (modelTransformation.length >= 16) {
    const m0 = toFiniteNumber(modelTransformation[0], null);
    const m1 = toFiniteNumber(modelTransformation[1], null);
    const m3 = toFiniteNumber(modelTransformation[3], null);
    const m4 = toFiniteNumber(modelTransformation[4], null);
    const m5 = toFiniteNumber(modelTransformation[5], null);
    const m7 = toFiniteNumber(modelTransformation[7], null);
    const hasNoSkew =
      m0 != null &&
      m1 != null &&
      m3 != null &&
      m4 != null &&
      m5 != null &&
      m7 != null &&
      Math.abs(m1) <= 1e-10 &&
      Math.abs(m4) <= 1e-10 &&
      Math.abs(m0) > GEO_TIFF_AXIS_EPS &&
      Math.abs(m5) > GEO_TIFF_AXIS_EPS;

    if (hasNoSkew) {
      originX = m3;
      originY = m7;
      pixelSizeX = m0;
      pixelSizeY = m5;
      derivedFrom = "model-transformation";
    }
  }

  if (
    derivedFrom == null &&
    modelTiepoint.length >= 6 &&
    modelPixelScale.length >= 2
  ) {
    const tieI = toFiniteNumber(modelTiepoint[0], 0);
    const tieJ = toFiniteNumber(modelTiepoint[1], 0);
    const tieX = toFiniteNumber(modelTiepoint[3], null);
    const tieY = toFiniteNumber(modelTiepoint[4], null);
    const scaleX = Math.abs(toFiniteNumber(modelPixelScale[0], 0));
    const scaleY = Math.abs(toFiniteNumber(modelPixelScale[1], 0));

    if (tieX != null && tieY != null && scaleX > 0 && scaleY > 0) {
      // Xgeo = tieX + (col - tieI) * scaleX
      // Ygeo = tieY - (row - tieJ) * scaleY (north-up convention)
      pixelSizeX = scaleX;
      pixelSizeY = -scaleY;
      originX = tieX - tieI * pixelSizeX;
      originY = tieY - tieJ * pixelSizeY;
      derivedFrom = "model-tiepoint-scale";
    }
  }

  if (
    originX == null ||
    originY == null ||
    pixelSizeX == null ||
    pixelSizeY == null
  ) {
    const origin = typeof image.getOrigin === "function" ? image.getOrigin() : null;
    const resolution = typeof image.getResolution === "function" ? image.getResolution() : null;
    const ox = toFiniteNumber(origin?.[0], null);
    const oy = toFiniteNumber(origin?.[1], null);
    const rx = toFiniteNumber(resolution?.[0], null);
    const ry = toFiniteNumber(resolution?.[1], null);
    if (ox != null && oy != null && rx != null && ry != null && Math.abs(rx) > 0 && Math.abs(ry) > 0) {
      originX = ox;
      originY = oy;
      pixelSizeX = rx;
      pixelSizeY = ry;
      derivedFrom = "origin-resolution";
    }
  }

  if (
    (originX == null ||
      originY == null ||
      pixelSizeX == null ||
      pixelSizeY == null ||
      Math.abs(pixelSizeX) <= GEO_TIFF_AXIS_EPS ||
      Math.abs(pixelSizeY) <= GEO_TIFF_AXIS_EPS) &&
    geoBounds
  ) {
    const boundsAffine = deriveAffineFromGeoBounds(geoBounds, width, height);
    if (boundsAffine) {
      originX = boundsAffine.originX;
      originY = boundsAffine.originY;
      pixelSizeX = boundsAffine.pixelSizeX;
      pixelSizeY = boundsAffine.pixelSizeY;
      derivedFrom = "image-bounds";
    }
  }

  if (
    geoBounds &&
    originX != null &&
    originY != null &&
    pixelSizeX != null &&
    pixelSizeY != null &&
    Math.abs(pixelSizeX) > GEO_TIFF_AXIS_EPS &&
    Math.abs(pixelSizeY) > GEO_TIFF_AXIS_EPS
  ) {
    const x0 = originX;
    const y0 = originY;
    const x1 = originX + (width - 1) * pixelSizeX;
    const y1 = originY + (height - 1) * pixelSizeY;
    const derivedMinX = Math.min(x0, x1);
    const derivedMaxX = Math.max(x0, x1);
    const derivedMinY = Math.min(y0, y1);
    const derivedMaxY = Math.max(y0, y1);

    const spanX = Math.max(Math.abs(geoBounds.maxX - geoBounds.minX), 1);
    const spanY = Math.max(Math.abs(geoBounds.maxY - geoBounds.minY), 1);
    const mismatch =
      Math.max(
        Math.abs(derivedMinX - geoBounds.minX) / spanX,
        Math.abs(derivedMaxX - geoBounds.maxX) / spanX,
        Math.abs(derivedMinY - geoBounds.minY) / spanY,
        Math.abs(derivedMaxY - geoBounds.maxY) / spanY
      );

    if (!Number.isFinite(mismatch) || mismatch > GEO_TIFF_BOUNDS_MISMATCH_RATIO) {
      const boundsAffine = deriveAffineFromGeoBounds(geoBounds, width, height, {
        x: Math.sign(pixelSizeX) || 1,
        y: Math.sign(pixelSizeY) || -1,
      });
      if (boundsAffine) {
        originX = boundsAffine.originX;
        originY = boundsAffine.originY;
        pixelSizeX = boundsAffine.pixelSizeX;
        pixelSizeY = boundsAffine.pixelSizeY;
        derivedFrom = "image-bounds-corrected";
      }
    }
  }

  if (
    originX == null ||
    originY == null ||
    pixelSizeX == null ||
    pixelSizeY == null ||
    Math.abs(pixelSizeX) <= 1e-15 ||
    Math.abs(pixelSizeY) <= 1e-15
  ) {
    return null;
  }

  const coordinateSpace = classifyGeoTiffCoordinateSpace(
    geoKeys,
    originX,
    originY,
    pixelSizeX,
    pixelSizeY
  );

  const pixelToGeo = (col, row) => ({
    x: originX + col * pixelSizeX,
    y: originY + row * pixelSizeY,
  });
  const corners = [
    pixelToGeo(0, 0),
    pixelToGeo(width - 1, 0),
    pixelToGeo(width - 1, height - 1),
    pixelToGeo(0, height - 1),
  ];

  const latLngCorners = corners.map((corner) => {
    if (coordinateSpace === "web-mercator-meters") {
      return {
        longitude: mercatorMetersToLongitude(corner.x),
        latitude: mercatorMetersToLatitude(corner.y),
      };
    }
    if (coordinateSpace === "geographic") {
      return {
        longitude: corner.x,
        latitude: corner.y,
      };
    }
    return null;
  });

  const validLatLngCorners = latLngCorners.filter(Boolean);
  const west =
    validLatLngCorners.length === 4
      ? Math.min(...validLatLngCorners.map((corner) => corner.longitude))
      : null;
  const east =
    validLatLngCorners.length === 4
      ? Math.max(...validLatLngCorners.map((corner) => corner.longitude))
      : null;
  const south =
    validLatLngCorners.length === 4
      ? Math.min(...validLatLngCorners.map((corner) => corner.latitude))
      : null;
  const north =
    validLatLngCorners.length === 4
      ? Math.max(...validLatLngCorners.map((corner) => corner.latitude))
      : null;

  return {
    mode: "geotiffAffine",
    coordinateSpace,
    derivedFrom,
    originX,
    originY,
    pixelSizeX,
    pixelSizeY,
    // Backward-compat aliases used by existing call sites.
    originLon: originX,
    originLat: originY,
    pixelSizeLon: pixelSizeX,
    pixelSizeLat: pixelSizeY,
    widthPx: width,
    heightPx: height,
    west,
    east,
    south,
    north,
    geoBounds,
  };
};

const loadGeoTiffRasters = async (
  url,
  { apiKey, fetchImpl = fetch } = {}
) => {
  if (!url) return null;

  const requestUrl = withGoogleApiKeyIfNeeded(url, apiKey);
  logSolarUtilsInfo("GeoTIFF download start", {
    sourceUrlHost: (() => {
      try {
        return new URL(requestUrl).host;
      } catch (error) {
        return "invalid-url";
      }
    })(),
  });
  const response = await fetchImpl(requestUrl);
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    logSolarUtilsWarn("GeoTIFF download failed", {
      status: response.status,
      body: text,
    });
    throw new Error(`Google Solar GeoTIFF download failed (${response.status}): ${text}`);
  }

  const buffer = await response.arrayBuffer();
  logSolarUtilsInfo("GeoTIFF download success", {
    bytes: buffer.byteLength,
  });
  const { fromArrayBuffer } = await getGeoTiffModule();
  const tiff = await fromArrayBuffer(buffer);
  const image = await tiff.getImage();
  const geoKeys = typeof image.getGeoKeys === "function" ? image.getGeoKeys() : null;
  const rasters = await image.readRasters({ interleave: false });
  const bands = Array.isArray(rasters) ? rasters : [];

  if (!bands.length || !bands[0] || !Number.isFinite(bands[0].length) || bands[0].length === 0) {
    throw new Error("Google Solar GeoTIFF did not contain readable raster data.");
  }

  const noDataFromApi =
    typeof image.getGDALNoData === "function" ? image.getGDALNoData() : null;
  const noDataFromDirectory = image.fileDirectory?.GDAL_NODATA ?? null;
  const noDataValue =
    parseGeoTiffNoData(noDataFromApi) ?? parseGeoTiffNoData(noDataFromDirectory);

  const width = image.getWidth();
  const height = image.getHeight();
  const geoReference = deriveGeoReferenceFromGeoTiffImage(image, geoKeys);

  logSolarUtilsInfo("GeoTIFF raster parsed", {
    width,
    height,
    noDataValue,
    bandCount: bands.length,
    hasGeoReference: Boolean(geoReference),
    coordinateSpace: geoReference?.coordinateSpace || null,
    geoKeys: geoKeys || null,
  });

  return {
    width,
    height,
    bands,
    noDataValue,
    geoReference,
  };
};

const normalizeBandToByteRange = (band, width, height) => {
  const len = width * height;
  const out = new Uint8ClampedArray(len);
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < len; i++) {
    const v = Number(band[i]);
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }

  const scale = max > min ? 255 / (max - min) : 1;
  for (let i = 0; i < len; i++) {
    const v = Number(band[i]);
    if (!Number.isFinite(v)) {
      out[i] = 0;
      continue;
    }
    const byteValue =
      max > 255 || min < 0
        ? Math.max(0, Math.min(255, Math.round((v - min) * scale)))
        : Math.max(0, Math.min(255, Math.round(v)));
    out[i] = byteValue;
  }
  return out;
};

const buildRgbaFromGeoTiffBands = (bands, width, height) => {
  const len = width * height;
  const rgba = new Uint8ClampedArray(len * 4);
  const rBand = bands?.[0] || null;
  const gBand = bands?.[1] || bands?.[0] || null;
  const bBand = bands?.[2] || bands?.[0] || null;

  if (!rBand || !gBand || !bBand) {
    throw new Error("Google Solar RGB GeoTIFF did not contain required bands.");
  }

  const rBytes = normalizeBandToByteRange(rBand, width, height);
  const gBytes = normalizeBandToByteRange(gBand, width, height);
  const bBytes = normalizeBandToByteRange(bBand, width, height);

  for (let i = 0; i < len; i++) {
    const j = i * 4;
    rgba[j] = rBytes[i];
    rgba[j + 1] = gBytes[i];
    rgba[j + 2] = bBytes[i];
    rgba[j + 3] = 255;
  }

  return rgba;
};

const withGoogleApiKeyIfNeeded = (url, apiKey) => {
  if (!url) return "";
  if (!apiKey) return url;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const isGoogleSolarHost =
      host === "solar.googleapis.com" || host.endsWith(".solar.googleapis.com");

    if (isGoogleSolarHost && !parsed.searchParams.has("key")) {
      parsed.searchParams.set("key", apiKey);
      return parsed.toString();
    }
  } catch (error) {
    return url;
  }

  return url;
};
const googleSolarRgbTilePromiseCache = new Map();

const loadSingleBandGeoTiff = async (
  url,
  { apiKey, fetchImpl = fetch } = {}
) => {
  const raster = await loadGeoTiffRasters(url, { apiKey, fetchImpl });
  const firstBand = raster?.bands?.[0] ?? null;

  if (!firstBand || !Number.isFinite(firstBand.length) || firstBand.length === 0) {
    throw new Error("Google Solar GeoTIFF did not contain readable raster data.");
  }

  return {
    width: raster.width,
    height: raster.height,
    values: firstBand,
    noDataValue: raster.noDataValue,
    geoReference: raster.geoReference || null,
  };
};

export async function loadGoogleSolarRgbTileData(
  dataLayers,
  { apiKey, fetchImpl = fetch } = {}
) {
  const rgbUrl = dataLayers?.rgbUrl;
  if (!rgbUrl) {
    throw new Error("Google Solar dataLayers response does not include rgbUrl.");
  }

  const sourceUrl = withGoogleApiKeyIfNeeded(rgbUrl, apiKey);
  if (googleSolarRgbTilePromiseCache.has(sourceUrl)) {
    return googleSolarRgbTilePromiseCache.get(sourceUrl);
  }

  const inFlight = (async () => {
    const raster = await loadGeoTiffRasters(sourceUrl, { apiKey, fetchImpl });
    if (!raster || raster.width < 2 || raster.height < 2) {
      throw new Error("Google Solar RGB raster is unavailable or invalid.");
    }

    const rgba = buildRgbaFromGeoTiffBands(raster.bands, raster.width, raster.height);
    const mapGeoReference = raster.geoReference
      ? {
          ...raster.geoReference,
          provider: "google-solar-rgb",
        }
      : null;

    return {
      width: raster.width,
      height: raster.height,
      rgba,
      mapGeoReference,
      noDataValue: raster.noDataValue,
      sourceUrl,
    };
  })();

  googleSolarRgbTilePromiseCache.set(sourceUrl, inFlight);
  try {
    return await inFlight;
  } catch (error) {
    googleSolarRgbTilePromiseCache.delete(sourceUrl);
    throw error;
  }
}

export async function loadGoogleSolarMaskTileData(
  dataLayers,
  { apiKey, fetchImpl = fetch } = {}
) {
  const maskUrl = dataLayers?.maskUrl;
  if (!maskUrl) {
    throw new Error("Google Solar dataLayers response does not include maskUrl.");
  }

  const raster = await loadSingleBandGeoTiff(maskUrl, { apiKey, fetchImpl });
  if (!raster || raster.width < 2 || raster.height < 2) {
    throw new Error("Google Solar mask raster is unavailable or invalid.");
  }

  const len = raster.width * raster.height;
  const mask = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    const value = Number(raster.values[i]);
    mask[i] =
      !isNoDataValue(value, raster.noDataValue) && value > 0
        ? 1
        : 0;
  }

  const mapGeoReference = raster.geoReference
    ? {
        ...raster.geoReference,
        provider: "google-solar-mask",
      }
    : null;

  return {
    width: raster.width,
    height: raster.height,
    mask,
    noDataValue: raster.noDataValue,
    mapGeoReference,
    sourceUrl: withGoogleApiKeyIfNeeded(maskUrl, apiKey),
  };
}

const buildSolarApiUrl = (path, params, apiKey) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    search.set(key, String(value));
  });
  search.set("key", apiKey);
  return `https://solar.googleapis.com/v1/${path}?${search.toString()}`;
};

export async function fetchGoogleSolarBuildingInsights(
  latitude,
  longitude,
  {
    apiKey,
    requiredQuality = "HIGH",
    exactQualityRequired = false,
    experiments = [],
    fetchImpl = fetch,
  } = {}
) {
  if (!apiKey) {
    throw new Error("Missing Google Maps API key for Solar API calls.");
  }

  const lat = toFiniteNumber(latitude, null);
  const lng = toFiniteNumber(longitude, null);
  if (lat == null || lng == null) {
    throw new Error("Missing latitude/longitude for Solar building insights request.");
  }

  logSolarUtilsInfo("fetchGoogleSolarBuildingInsights request", {
    latitude: lat,
    longitude: lng,
    requiredQuality,
    exactQualityRequired,
  });

  const params = {
    "location.latitude": lat,
    "location.longitude": lng,
    requiredQuality,
    exactQualityRequired,
  };

  if (Array.isArray(experiments)) {
    experiments.forEach((experiment) => {
      if (!experiment) return;
      params["experiments"] = experiment;
    });
  }

  const response = await fetchImpl(
    buildSolarApiUrl("buildingInsights:findClosest", params, apiKey)
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    logSolarUtilsWarn("fetchGoogleSolarBuildingInsights failed", {
      status: response.status,
      body: text,
      request: {
        latitude: lat,
        longitude: lng,
        requiredQuality,
        exactQualityRequired,
      },
    });
    throw new Error(`Google Solar buildingInsights failed (${response.status}): ${text}`);
  }

  const payload = await response.json();
  logSolarUtilsInfo("fetchGoogleSolarBuildingInsights success", {
    imageryQuality: payload?.imageryQuality || null,
    hasSolarPotential: Boolean(payload?.solarPotential),
    roofSegmentCount: Array.isArray(payload?.solarPotential?.roofSegmentStats)
      ? payload.solarPotential.roofSegmentStats.length
      : 0,
  });
  return payload;
}

export async function fetchGoogleSolarDataLayers(
  latitude,
  longitude,
  {
    apiKey,
    radiusMeters = 100,
    view = "FULL_LAYERS",
    requiredQuality = "HIGH",
    exactQualityRequired = false,
    pixelSizeMeters = 0.1,
    experiments = [],
    fetchImpl = fetch,
  } = {}
) {
  if (!apiKey) {
    throw new Error("Missing Google Maps API key for Solar API calls.");
  }

  const lat = toFiniteNumber(latitude, null);
  const lng = toFiniteNumber(longitude, null);
  if (lat == null || lng == null) {
    throw new Error("Missing latitude/longitude for Solar data layers request.");
  }

  const params = {
    "location.latitude": lat,
    "location.longitude": lng,
    radiusMeters,
    view,
    requiredQuality,
    exactQualityRequired,
    pixelSizeMeters,
  };

  logSolarUtilsInfo("fetchGoogleSolarDataLayers request", {
    latitude: lat,
    longitude: lng,
    radiusMeters,
    view,
    requiredQuality,
    exactQualityRequired,
    pixelSizeMeters,
  });

  if (Array.isArray(experiments)) {
    experiments.forEach((experiment) => {
      if (!experiment) return;
      params["experiments"] = experiment;
    });
  }

  const response = await fetchImpl(
    buildSolarApiUrl("dataLayers:get", params, apiKey)
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    logSolarUtilsWarn("fetchGoogleSolarDataLayers failed", {
      status: response.status,
      body: text,
      request: {
        latitude: lat,
        longitude: lng,
        radiusMeters,
        view,
        requiredQuality,
        exactQualityRequired,
        pixelSizeMeters,
      },
    });
    throw new Error(`Google Solar dataLayers failed (${response.status}): ${text}`);
  }

  const payload = await response.json();
  logSolarUtilsInfo("fetchGoogleSolarDataLayers success", {
    hasDsmUrl: Boolean(payload?.dsmUrl),
    hasMaskUrl: Boolean(payload?.maskUrl),
    hasRgbUrl: Boolean(payload?.rgbUrl),
    imageryQuality: payload?.imageryQuality || null,
  });
  return payload;
}

export function normalizeRoofSegmentStats(buildingInsights) {
  const rawSegments = buildingInsights?.solarPotential?.roofSegmentStats;
  if (!Array.isArray(rawSegments)) return [];

  return rawSegments
    .map((segment, index) => {
      const explicitSegmentIndex = Number(segment?.segmentIndex);
      const segmentIndex = Number.isInteger(explicitSegmentIndex)
        ? explicitSegmentIndex
        : index;
      const centerLat = toFiniteNumber(segment?.center?.latitude, null);
      const centerLng = toFiniteNumber(segment?.center?.longitude, null);
      const pitchDegrees = toFiniteNumber(segment?.pitchDegrees, 0);
      const azimuthDegrees = toFiniteNumber(segment?.azimuthDegrees, 0);
      const planeHeightAtCenterMeters = toFiniteNumber(
        segment?.planeHeightAtCenterMeters,
        null
      );

      if (centerLat == null || centerLng == null || planeHeightAtCenterMeters == null) {
        return null;
      }

      return {
        segmentIndex,
        pitchDegrees,
        azimuthDegrees,
        planeHeightAtCenterMeters,
        center: {
          latitude: centerLat,
          longitude: centerLng,
        },
        boundingBox: {
          sw: {
            latitude: toFiniteNumber(segment?.boundingBox?.sw?.latitude, centerLat),
            longitude: toFiniteNumber(segment?.boundingBox?.sw?.longitude, centerLng),
          },
          ne: {
            latitude: toFiniteNumber(segment?.boundingBox?.ne?.latitude, centerLat),
            longitude: toFiniteNumber(segment?.boundingBox?.ne?.longitude, centerLng),
          },
        },
        stats: {
          areaMeters2: toFiniteNumber(segment?.stats?.areaMeters2, 0),
          groundAreaMeters2: toFiniteNumber(segment?.stats?.groundAreaMeters2, 0),
          sunshineQuantiles: Array.isArray(segment?.stats?.sunshineQuantiles)
            ? segment.stats.sunshineQuantiles
                .map((value) => toFiniteNumber(value, null))
                .filter((value) => value != null)
            : [],
        },
      };
    })
    .filter(Boolean);
}

export function pickRoofSegmentByOrientation(roofSegments, { tilt = 0, azimuth = 180 } = {}) {
  if (!Array.isArray(roofSegments) || roofSegments.length === 0) return null;

  const targetTilt = toFiniteNumber(tilt, 0);
  const targetAzimuth = toFiniteNumber(azimuth, 180);

  let best = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const segment of roofSegments) {
    const pitchDiff = Math.abs((segment?.pitchDegrees ?? 0) - targetTilt);
    const azimuthDiff = circularDiffDegrees(segment?.azimuthDegrees ?? 0, targetAzimuth);
    const score = pitchDiff + azimuthDiff * 0.45;
    if (score < bestScore) {
      best = segment;
      bestScore = score;
    }
  }

  return best || roofSegments[0];
}

const getSunshineQuantileReference = (buildingInsights, roofSegment) => {
  const roofQuantiles = Array.isArray(roofSegment?.stats?.sunshineQuantiles)
    ? roofSegment.stats.sunshineQuantiles
    : [];
  if (roofQuantiles.length > 0) {
    return roofQuantiles[Math.floor(roofQuantiles.length / 2)] ?? roofQuantiles[roofQuantiles.length - 1];
  }

  const wholeRoofQuantiles = Array.isArray(
    buildingInsights?.solarPotential?.wholeRoofStats?.sunshineQuantiles
  )
    ? buildingInsights.solarPotential.wholeRoofStats.sunshineQuantiles
    : [];

  if (wholeRoofQuantiles.length > 0) {
    return wholeRoofQuantiles[Math.floor(wholeRoofQuantiles.length / 2)] ?? wholeRoofQuantiles[wholeRoofQuantiles.length - 1];
  }

  return toFiniteNumber(buildingInsights?.solarPotential?.maxSunshineHoursPerYear, 0);
};

export function deriveSyntheticTofPercent(buildingInsights, roofSegment) {
  const segmentSun = getSunshineQuantileReference(buildingInsights, roofSegment);
  const roofMax = toFiniteNumber(buildingInsights?.solarPotential?.maxSunshineHoursPerYear, segmentSun);
  if (!Number.isFinite(segmentSun) || !Number.isFinite(roofMax) || roofMax <= 0) {
    return 0;
  }
  return Number((clamp(segmentSun / roofMax, 0, 1) * 100).toFixed(2));
}

export function deriveSyntheticGtiSeries(
  buildingInsights,
  { roofSegment, tilt = 0, azimuth = 180, year = new Date().getFullYear() } = {}
) {
  const referenceSunshine = getSunshineQuantileReference(buildingInsights, roofSegment);
  const annualReference = Math.max(1, toFiniteNumber(referenceSunshine, 1200));

  const roofPitch = toFiniteNumber(roofSegment?.pitchDegrees, tilt);
  const roofAzimuth = toFiniteNumber(roofSegment?.azimuthDegrees, azimuth);

  const tiltPenalty = 1 - clamp(Math.abs(roofPitch - tilt) / 90, 0, 0.55);
  const azPenalty = 1 - clamp(circularDiffDegrees(roofAzimuth, azimuth) / 180, 0, 0.55);
  const orientationFactor = clamp(tiltPenalty * azPenalty, 0.35, 1.15);

  const monthlyWeightSum = DEFAULT_MONTHLY_WEIGHTS.reduce((sum, value) => sum + value, 0);
  const hourCurve = Array.from({ length: 24 }, (_, hour) =>
    Math.max(0, Math.sin(((hour - 6) * Math.PI) / 12))
  );
  const hourWeightSum = hourCurve.reduce((sum, value) => sum + value, 0);

  const output = [];

  for (let month = 1; month <= 12; month++) {
    const days = getDaysInMonth(year, month);
    const monthWeight = DEFAULT_MONTHLY_WEIGHTS[month - 1] ?? 1;
    const monthEnergy = (annualReference * monthWeight) / Math.max(monthlyWeightSum, 1);
    const dayEnergy = monthEnergy / Math.max(days, 1);

    for (let day = 1; day <= days; day++) {
      for (let hour = 0; hour < 24; hour++) {
        const hourWeight = hourCurve[hour];
        if (hourWeight <= 0) continue;

        const gti = (dayEnergy * hourWeight * orientationFactor) / Math.max(hourWeightSum, 1);
        const timestamp = new Date(Date.UTC(year, month - 1, day, hour, 0, 0));
        output.push({
          month,
          day,
          hour,
          gti,
          datetime: timestamp.toISOString(),
          time: timestamp.toISOString(),
        });
      }
    }
  }

  return output;
}

export function deriveSolarInputsFromBuildingInsights(
  buildingInsights,
  { tilt = 0, azimuth = 180 } = {}
) {
  const roofSegments = normalizeRoofSegmentStats(buildingInsights);
  const selectedRoofSegment = pickRoofSegmentByOrientation(roofSegments, {
    tilt,
    azimuth,
  });

  const tof = deriveSyntheticTofPercent(buildingInsights, selectedRoofSegment);
  const gti = deriveSyntheticGtiSeries(buildingInsights, {
    roofSegment: selectedRoofSegment,
    tilt,
    azimuth,
  });

  return {
    tof,
    gti,
    roofSegments,
    selectedRoofSegment,
  };
}

export async function buildLidarFromGoogleSolarDataLayers(
  dataLayers,
  {
    apiKey,
    latitude,
    longitude,
    radiusMeters = 100,
    footprintWidthMeters = null,
    footprintHeightMeters = null,
    maxPoints = null,
    fetchImpl = fetch,
  } = {}
) {
  const anchorLat = toFiniteNumber(latitude, null);
  const anchorLng = toFiniteNumber(longitude, null);
  if (anchorLat == null || anchorLng == null) {
    logSolarUtilsWarn("buildLidarFromGoogleSolarDataLayers skipped: missing anchor lat/lng", {
      latitude,
      longitude,
    });
    return null;
  }

  const dsmUrl = dataLayers?.dsmUrl;
  if (!dsmUrl) {
    logSolarUtilsWarn("buildLidarFromGoogleSolarDataLayers skipped: missing dsmUrl");
    return null;
  }

  logSolarUtilsInfo("buildLidarFromGoogleSolarDataLayers start", {
    anchorLat,
    anchorLng,
    radiusMeters,
    footprintWidthMeters,
    footprintHeightMeters,
    maxPoints,
    hasMaskUrl: Boolean(dataLayers?.maskUrl),
  });

  const dsmRaster = await loadSingleBandGeoTiff(dsmUrl, { apiKey, fetchImpl });
  if (!dsmRaster?.values?.length) {
    logSolarUtilsWarn("buildLidarFromGoogleSolarDataLayers skipped: empty DSM raster");
    return null;
  }

  const width = dsmRaster.width;
  const height = dsmRaster.height;
  if (!Number.isFinite(width) || width < 2 || !Number.isFinite(height) || height < 2) {
    logSolarUtilsWarn("buildLidarFromGoogleSolarDataLayers skipped: invalid raster dimensions", {
      width,
      height,
    });
    return null;
  }

  const requestedRadius = Math.max(1, toFiniteNumber(radiusMeters, 100));
  const spanWidthMeters = requestedRadius * 2;
  const spanHeightMeters = requestedRadius * 2;
  const pixelSizeX = Math.max(
    GOOGLE_SOLAR_MIN_PIXEL_SIZE_METERS,
    spanWidthMeters / Math.max(width - 1, 1)
  );
  const pixelSizeY = Math.max(
    GOOGLE_SOLAR_MIN_PIXEL_SIZE_METERS,
    spanHeightMeters / Math.max(height - 1, 1)
  );

  const centerCol = (width - 1) * 0.5;
  const centerRow = (height - 1) * 0.5;
  const projectPixelToLocalMeters =
    getGeoReferenceLocalMetersProjector(dsmRaster.geoReference, anchorLat, anchorLng) ||
    ((col, row) => ({
      east: (col - centerCol) * pixelSizeX,
      north: (centerRow - row) * pixelSizeY,
    }));

  const halfFootprintWidth =
    toFiniteNumber(footprintWidthMeters, null) != null &&
    toFiniteNumber(footprintWidthMeters, null) > 1
      ? toFiniteNumber(footprintWidthMeters, null) * 0.5
      : null;
  const halfFootprintHeight =
    toFiniteNumber(footprintHeightMeters, null) != null &&
    toFiniteNumber(footprintHeightMeters, null) > 1
      ? toFiniteNumber(footprintHeightMeters, null) * 0.5
      : null;

  const isWithinMapFootprint = (east, north) => {
    if (halfFootprintWidth != null && Math.abs(east) > halfFootprintWidth) return false;
    if (halfFootprintHeight != null && Math.abs(north) > halfFootprintHeight) return false;
    return true;
  };

  let validCount = 0;
  let minZ = Number.POSITIVE_INFINITY;
  const noDataValue = dsmRaster.noDataValue;
  const values = dsmRaster.values;

  // Height baseline (Z = 0): the MINIMUM elevation around the data centroid (the
  // building center is local 0,0) -- NOT the global lowest pixel. The global min
  // is often a distant road / low ground that drags the whole model off its
  // baseline. We sample a window around the centroid and take a robust low
  // percentile of it (the "minimum point" but immune to a stray noise pixel).
  const CENTROID_BASELINE_RADIUS_METERS = 20;
  // Tight window right at the centroid -> "what is there" at the building center
  // (the roof/surface), used to vertically place the LiDAR on the house model.
  const CENTROID_PROBE_RADIUS_METERS = 3;
  const centroidSamples = [];
  const centerProbeSamples = [];

  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const localPoint = projectPixelToLocalMeters(col, row);
      if (!localPoint) continue;
      const east = localPoint.east;
      const north = localPoint.north;
      if (!isWithinMapFootprint(east, north)) continue;

      const idx = row * width + col;
      const zAbs = Number(values[idx]);
      if (isNoDataValue(zAbs, noDataValue)) continue;

      validCount += 1;
      if (zAbs < minZ) minZ = zAbs;

      if (
        Math.abs(east) <= CENTROID_BASELINE_RADIUS_METERS &&
        Math.abs(north) <= CENTROID_BASELINE_RADIUS_METERS
      ) {
        centroidSamples.push(zAbs);
      }
      if (
        Math.abs(east) <= CENTROID_PROBE_RADIUS_METERS &&
        Math.abs(north) <= CENTROID_PROBE_RADIUS_METERS
      ) {
        centerProbeSamples.push(zAbs);
      }
    }
  }

  if (!validCount || !Number.isFinite(minZ)) {
    logSolarUtilsWarn("buildLidarFromGoogleSolarDataLayers skipped: no valid DSM points", {
      width,
      height,
      noDataValue,
    });
    return null;
  }

  // Robust minimum of the centroid window (2nd percentile). Falls back to the
  // global minimum if nothing landed in the window (unexpected).
  let referenceZ;
  let referenceSource;
  if (centroidSamples.length) {
    centroidSamples.sort((a, b) => a - b);
    const pIdx = Math.min(
      centroidSamples.length - 1,
      Math.max(0, Math.round((centroidSamples.length - 1) * 0.02))
    );
    referenceZ = centroidSamples[pIdx];
    referenceSource = "centroid-min";
  } else {
    referenceZ = minZ;
    referenceSource = "global-min";
  }

  // Surface height AT the centroid (median of the tight center probe), relative to
  // the baseline. This is "what is there" at the building center -- the consumer
  // uses it to drop the LiDAR onto the house model at the right Z.
  let centroidHeight = 0;
  if (centerProbeSamples.length) {
    centerProbeSamples.sort((a, b) => a - b);
    const mIdx = Math.floor((centerProbeSamples.length - 1) / 2);
    centroidHeight = centerProbeSamples[mIdx] - referenceZ;
  } else if (centroidSamples.length) {
    centroidSamples.sort((a, b) => a - b);
    const mIdx = Math.floor((centroidSamples.length - 1) / 2);
    centroidHeight = centroidSamples[mIdx] - referenceZ;
  }

  logSolarUtilsInfo("buildLidarFromGoogleSolarDataLayers height baseline", {
    referenceSource,
    referenceZ,
    globalMinZ: minZ,
    centroidSampleCount: centroidSamples.length,
    centroidRadiusMeters: CENTROID_BASELINE_RADIUS_METERS,
    centroidHeight,
  });

  const maxPointBudget =
    Number.isFinite(maxPoints) && maxPoints > 0 ? Math.floor(maxPoints) : null;
  const stride =
    maxPointBudget != null && validCount > maxPointBudget
      ? Math.max(1, Math.ceil(Math.sqrt(validCount / maxPointBudget)))
      : 1;
  const outputPoints = [];

  for (let row = 0; row < height; row += stride) {
    for (let col = 0; col < width; col += stride) {
      const localPoint = projectPixelToLocalMeters(col, row);
      if (!localPoint) continue;
      const east = localPoint.east;
      const north = localPoint.north;
      if (!isWithinMapFootprint(east, north)) continue;

      const idx = row * width + col;
      const zAbs = Number(values[idx]);
      if (isNoDataValue(zAbs, noDataValue)) continue;

      outputPoints.push({
        x: east,
        y: north,
        z: zAbs - referenceZ,
      });
    }
  }

  if (!outputPoints.length) {
    logSolarUtilsWarn("buildLidarFromGoogleSolarDataLayers skipped: output points empty after sampling", {
      validCount,
      stride,
    });
    return null;
  }

  // Build a STRUCTURED grid straight from the DSM raster (lightly strided to a
  // vertex budget) and keep the row/col topology intact. The DSM is already a
  // clean "top surface" raster, so meshing it directly -- exactly like the
  // offline dsm_to_vtu.py tool -- avoids the lossy "points -> coarse re-bin ->
  // max" step that turned roofs/trees into spikes and blobs.
  //
  // Convention matches the mesh worker's helpers: index = i * GH + j, where
  // i is the column (x/east) and j is the row (y/north). z is NaN for NoData.
  // Mirror dsm_to_vtu.py, whose crispness comes from gridding at the native DSM
  // resolution (0.125 m), NOT from heavy smoothing. So we keep the grid near the
  // raster's own pixel size (~0.18 m target) and only stride down if the raster
  // is finer than that or would blow past the mesh-size cap. This preserves roof
  // planes/ridges and tree spikes across the whole area instead of melting them.
  const TARGET_CELL_METERS = 0.18;
  const nativePixelMeters = Math.max(
    GOOGLE_SOLAR_MIN_PIXEL_SIZE_METERS,
    Math.min(pixelSizeX, pixelSizeY)
  );
  const GRID_MAX_DIM = 1100; // hard cap per axis (~1.2M cells worst case)
  let gridStride = Math.max(1, Math.round(TARGET_CELL_METERS / nativePixelMeters));
  while (
    Math.floor((width - 1) / gridStride) + 1 > GRID_MAX_DIM ||
    Math.floor((height - 1) / gridStride) + 1 > GRID_MAX_DIM
  ) {
    gridStride += 1;
  }
  const GW = Math.floor((width - 1) / gridStride) + 1;
  const GH = Math.floor((height - 1) / gridStride) + 1;

  const gridX = new Float32Array(GW * GH);
  const gridY = new Float32Array(GW * GH);
  const gridZ = new Float32Array(GW * GH);
  let gridValidCount = 0;

  for (let i = 0; i < GW; i++) {
    const col = Math.min(width - 1, i * gridStride);
    for (let j = 0; j < GH; j++) {
      const rowPx = Math.min(height - 1, j * gridStride);
      const gi = i * GH + j;

      const lp = projectPixelToLocalMeters(col, rowPx);
      if (!lp) {
        gridX[gi] = 0;
        gridY[gi] = 0;
        gridZ[gi] = Number.NaN;
        continue;
      }
      gridX[gi] = lp.east;
      gridY[gi] = lp.north;

      const zAbs = Number(values[rowPx * width + col]);
      if (isNoDataValue(zAbs, noDataValue)) {
        gridZ[gi] = Number.NaN;
        continue;
      }
      gridZ[gi] = zAbs - referenceZ;
      gridValidCount += 1;
    }
  }

  const dsmGrid =
    gridValidCount > 0
      ? {
          width: GW,
          height: GH,
          stride: gridStride,
          x: gridX,
          y: gridY,
          z: gridZ,
          validCount: gridValidCount,
        }
      : null;

  logSolarUtilsInfo("buildLidarFromGoogleSolarDataLayers success", {
    outputPointCount: outputPoints.length,
    validPointCount: validCount,
    width,
    height,
    stride,
    pixelSizeX,
    pixelSizeY,
    minZ,
  });

  return {
    source: "google-solar-dsm",
    lat: anchorLat,
    lon: anchorLng,
    points: [
      {
        source: "google-solar-dsm",
        points: outputPoints,
      },
    ],
    // Structured DSM grid for direct meshing (preferred path). Falls back to the
    // flat point cloud above when this is null (e.g. synthetic data).
    dsmGrid,
    // Height baseline used as Z = 0 (roof lowest point when the mask is available).
    heightReference: {
      value: referenceZ,
      source: referenceSource,
      globalMinZ: minZ,
      centroidHeight,
    },
    roofSegmentStats: [],
    footprint: {
      widthMeters:
        halfFootprintWidth != null ? halfFootprintWidth * 2 : spanWidthMeters,
      heightMeters:
        halfFootprintHeight != null ? halfFootprintHeight * 2 : spanHeightMeters,
    },
    raster: {
      width,
      height,
      validPointCount: validCount,
      sampledPointCount: outputPoints.length,
      samplingStride: stride,
      isDense: stride === 1,
      noDataValue,
      spanWidthMeters,
      spanHeightMeters,
      pixelSizeX,
      pixelSizeY,
      geoReference: dsmRaster.geoReference || null,
    },
  };
}

export function buildSyntheticLidarFromBuildingInsights(
  buildingInsights,
  {
    latitude,
    longitude,
    includeGround = true,
    maxPoints = 50000,
    sampleStepMeters = 1.25,
    footprintWidthMeters = null,
    footprintHeightMeters = null,
    groundStepMeters = null,
  } = {}
) {
  const anchorLat =
    toFiniteNumber(latitude, null) ?? toFiniteNumber(buildingInsights?.center?.latitude, null);
  const anchorLng =
    toFiniteNumber(longitude, null) ?? toFiniteNumber(buildingInsights?.center?.longitude, null);

  if (anchorLat == null || anchorLng == null) return null;

  const roofSegments = normalizeRoofSegmentStats(buildingInsights);
  if (!roofSegments.length) {
    if (!includeGround) return null;

    const footprintWidth = toFiniteNumber(footprintWidthMeters, null);
    const footprintHeight = toFiniteNumber(footprintHeightMeters, null);
    if (
      footprintWidth == null ||
      footprintWidth <= 1 ||
      footprintHeight == null ||
      footprintHeight <= 1
    ) {
      return null;
    }

    const groundStep = Math.max(
      1.5,
      toFiniteNumber(groundStepMeters, null) || Math.max(3, sampleStepMeters * 2.5)
    );
    const halfWidth = footprintWidth * 0.5;
    const halfHeight = footprintHeight * 0.5;
    const outputPoints = [];

    for (let east = -halfWidth; east <= halfWidth; east += groundStep) {
      for (let north = -halfHeight; north <= halfHeight; north += groundStep) {
        outputPoints.push({ x: east, y: north, z: 0 });
      }
    }

    return {
      source: "google-solar",
      lat: anchorLat,
      lon: anchorLng,
      points: [
        {
          source: "google-solar-ground-only",
          points: outputPoints,
        },
      ],
      roofSegmentStats: [],
      footprint: {
        widthMeters: footprintWidth,
        heightMeters: footprintHeight,
      },
    };
  }

  const points = [];
  const step = Math.max(0.5, sampleStepMeters);

  let minEast = Number.POSITIVE_INFINITY;
  let maxEast = Number.NEGATIVE_INFINITY;
  let minNorth = Number.POSITIVE_INFINITY;
  let maxNorth = Number.NEGATIVE_INFINITY;

  for (const segment of roofSegments) {
    const center = latLngToLocalMeters(
      segment.center.latitude,
      segment.center.longitude,
      anchorLat,
      anchorLng
    );

    const sw = latLngToLocalMeters(
      segment.boundingBox.sw.latitude,
      segment.boundingBox.sw.longitude,
      anchorLat,
      anchorLng
    );

    const ne = latLngToLocalMeters(
      segment.boundingBox.ne.latitude,
      segment.boundingBox.ne.longitude,
      anchorLat,
      anchorLng
    );

    const eastMin = Math.min(sw.east, ne.east);
    const eastMax = Math.max(sw.east, ne.east);
    const northMin = Math.min(sw.north, ne.north);
    const northMax = Math.max(sw.north, ne.north);

    minEast = Math.min(minEast, eastMin);
    maxEast = Math.max(maxEast, eastMax);
    minNorth = Math.min(minNorth, northMin);
    maxNorth = Math.max(maxNorth, northMax);

    const pitchRad = (toFiniteNumber(segment.pitchDegrees, 0) * Math.PI) / 180;
    const azimuthRad = (toFiniteNumber(segment.azimuthDegrees, 0) * Math.PI) / 180;
    const slope = Math.tan(pitchRad);
    const downEast = Math.sin(azimuthRad);
    const downNorth = Math.cos(azimuthRad);

    for (let east = eastMin; east <= eastMax; east += step) {
      for (let north = northMin; north <= northMax; north += step) {
        const alongDownSlope =
          (east - center.east) * downEast + (north - center.north) * downNorth;
        const zAbs = segment.planeHeightAtCenterMeters - slope * alongDownSlope;
        points.push({ x: east, y: north, z: zAbs });
      }
    }
  }

  if (!points.length) return null;

  if (
    includeGround &&
    Number.isFinite(minEast) &&
    Number.isFinite(maxEast) &&
    Number.isFinite(minNorth) &&
    Number.isFinite(maxNorth)
  ) {
    const footprintWidth = toFiniteNumber(footprintWidthMeters, null);
    const footprintHeight = toFiniteNumber(footprintHeightMeters, null);
    if (footprintWidth != null && footprintWidth > 1) {
      const halfWidth = footprintWidth * 0.5;
      minEast = Math.min(minEast, -halfWidth);
      maxEast = Math.max(maxEast, halfWidth);
    }
    if (footprintHeight != null && footprintHeight > 1) {
      const halfHeight = footprintHeight * 0.5;
      minNorth = Math.min(minNorth, -halfHeight);
      maxNorth = Math.max(maxNorth, halfHeight);
    }

    const groundZ = Math.min(...points.map((point) => point.z)) - 2;
    const configuredGroundStep = toFiniteNumber(groundStepMeters, null);
    const groundStep = Math.max(
      1.5,
      configuredGroundStep != null ? configuredGroundStep : Math.max(3, step * 2.5)
    );

    for (let east = minEast; east <= maxEast; east += groundStep) {
      for (let north = minNorth; north <= maxNorth; north += groundStep) {
        points.push({ x: east, y: north, z: groundZ });
      }
    }
  }

  const minZ = Math.min(...points.map((point) => point.z));
  const normalized = points.map((point) => ({
    x: point.x,
    y: point.y,
    z: point.z - minZ,
  }));

  let outputPoints = normalized;
  if (normalized.length > maxPoints) {
    const stride = Math.ceil(normalized.length / maxPoints);
    outputPoints = normalized.filter((_, index) => index % stride === 0);
  }

  return {
    source: "google-solar",
    lat: anchorLat,
    lon: anchorLng,
    points: [
      {
        source: "google-solar-derived",
        points: outputPoints,
      },
    ],
    roofSegmentStats: roofSegments,
    footprint: {
      widthMeters: toFiniteNumber(footprintWidthMeters, null),
      heightMeters: toFiniteNumber(footprintHeightMeters, null),
    },
  };
}

export function buildGoogleSolarSummary(buildingInsights, dataLayers = null) {
  const potential = buildingInsights?.solarPotential || {};
  return {
    building: {
      name: buildingInsights?.name || null,
      center: buildingInsights?.center || null,
      boundingBox: buildingInsights?.boundingBox || null,
      imageryDate: buildingInsights?.imageryDate || null,
      imageryProcessedDate: buildingInsights?.imageryProcessedDate || null,
      imageryQuality: buildingInsights?.imageryQuality || null,
      postalCode: buildingInsights?.postalCode || null,
      administrativeArea: buildingInsights?.administrativeArea || null,
      regionCode: buildingInsights?.regionCode || null,
    },
    solarPotential: {
      maxArrayPanelsCount: toFiniteNumber(potential?.maxArrayPanelsCount, 0),
      maxArrayAreaMeters2: toFiniteNumber(potential?.maxArrayAreaMeters2, 0),
      maxSunshineHoursPerYear: toFiniteNumber(potential?.maxSunshineHoursPerYear, 0),
      panelCapacityWatts: toFiniteNumber(potential?.panelCapacityWatts, 0),
      panelHeightMeters: toFiniteNumber(potential?.panelHeightMeters, 0),
      panelWidthMeters: toFiniteNumber(potential?.panelWidthMeters, 0),
      panelLifetimeYears: toFiniteNumber(potential?.panelLifetimeYears, 0),
      carbonOffsetFactorKgPerMwh: toFiniteNumber(potential?.carbonOffsetFactorKgPerMwh, 0),
      wholeRoofStats: potential?.wholeRoofStats || null,
      buildingStats: potential?.buildingStats || null,
      roofSegmentStats: normalizeRoofSegmentStats(buildingInsights),
      solarPanels: Array.isArray(potential?.solarPanels)
        ? potential.solarPanels
        : [],
      solarPanelConfigs: Array.isArray(potential?.solarPanelConfigs)
        ? potential.solarPanelConfigs
        : [],
      financialAnalyses: Array.isArray(potential?.financialAnalyses)
        ? potential.financialAnalyses
        : [],
    },
    dataLayers: dataLayers
      ? {
          imageryDate: dataLayers?.imageryDate || null,
          imageryProcessedDate: dataLayers?.imageryProcessedDate || null,
          imageryQuality: dataLayers?.imageryQuality || null,
          dsmUrl: dataLayers?.dsmUrl || null,
          rgbUrl: dataLayers?.rgbUrl || null,
          maskUrl: dataLayers?.maskUrl || null,
          annualFluxUrl: dataLayers?.annualFluxUrl || null,
          monthlyFluxUrl: dataLayers?.monthlyFluxUrl || null,
          hourlyShadeUrls: Array.isArray(dataLayers?.hourlyShadeUrls)
            ? dataLayers.hourlyShadeUrls
            : [],
        }
      : null,
  };
}
